import { useState } from 'react';
import {
  Button,
  Stack,
  CircularProgress,
  Menu,
  MenuItem,
  Switch,
  ListItemText,
  Typography,
  Chip,
} from '@mui/material';
import SalesTable from './SalesTable';
import PageLayout from '@shared/shell/Layout/PageLayout';
import { startOfWeek, endOfWeek } from 'date-fns';
import { Product } from '@domain/product/models/Product';
import ProductsFilter from '@shared/ui/filters/ProductsFilter';
import { Paper } from '@shared/ui/common';
import ActiveFilters from '@shared/ui/filters/ActiveFilters';
import { SalesChart } from '@features/index';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@shared/hooks/useToast';
import { container } from '@infrastructure/container';
import { useProductMarketplaces } from '@features/products';
import { getSalesFilterCountryOptions } from '../constants/salesFilterCountries';
import { Icon } from '@shared/icons';
import { SalesOnboardingOverlay } from '@features/tutorial';

const SHOW_NUMBER_OF_WEEKS = 12;
const SHOW_NUMBER_OF_FORECAST_WEEKS = 26;

const countryOptions = getSalesFilterCountryOptions();

const Sales = () => {
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  const [isExporting, setIsExporting] = useState(false);
  const [selectedMarketplaceIds, setSelectedMarketplaceIds] = useState<Set<string>>(new Set());
  const [selectedCountryCodes, setSelectedCountryCodes] = useState<Set<string>>(new Set());
  const [countryMenuAnchor, setCountryMenuAnchor] = useState<null | HTMLElement>(null);
  const { marketplaces } = useProductMarketplaces();

  const toggleMarketplace = (marketplaceId: string) => {
    setSelectedMarketplaceIds((prev) => {
      const next = new Set(prev);
      if (next.has(marketplaceId)) {
        next.delete(marketplaceId);
      } else {
        next.add(marketplaceId);
      }
      return next;
    });
  };

  const toggleCountry = (countryCode: string) => {
    setSelectedCountryCodes((prev) => {
      const next = new Set(prev);
      if (next.has(countryCode)) {
        next.delete(countryCode);
      } else {
        next.add(countryCode);
      }
      return next;
    });
  };

  const selectedMarketplaceIdsArray = selectedMarketplaceIds.size > 0 ? Array.from(selectedMarketplaceIds) : undefined;
  const selectedCountryCodesArray = selectedCountryCodes.size > 0 ? Array.from(selectedCountryCodes) : undefined;

  const [dateRange] = useState(() => {
    const endDate = endOfWeek(new Date());
    const startDate = startOfWeek(endDate);
    endDate.setDate(endDate.getDate() + (7 * SHOW_NUMBER_OF_FORECAST_WEEKS));
    startDate.setDate(startDate.getDate() - (7 * (SHOW_NUMBER_OF_WEEKS - 1)));
    return { startDate, endDate };
  });

  const navigateToProduct = (product: Product) => {
    navigate(`/products/${product.id}?tab=sales-timetravel`);
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const result = await container.salesRepository.exportSalesCSV(
        dateRange.startDate,
        dateRange.endDate,
        selectedMarketplaceIdsArray,
        selectedCountryCodesArray,
      );

      if (result.status === 'error') {
        showError(result.error?.message || 'Failed to export sales CSV');
        return;
      }

      const blob = result.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `sales_export_${dateRange.startDate.toISOString().split('T')[0]}_${dateRange.endDate.toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      success('Sales data exported successfully');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to export sales CSV');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <PageLayout title="Sales">
      <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ maxWidth: '100%', py: 2 }}>
        {marketplaces.map((mp) => {
          const isOn = selectedMarketplaceIds.has(mp.id);
          return (
            <Chip
              key={mp.id}
              label={mp.name}
              clickable
              color={isOn ? 'primary' : 'default'}
              variant={isOn ? 'filled' : 'outlined'}
              onClick={() => toggleMarketplace(mp.id)}
              size="small"
            />
          );
        })}
        {/* <Button
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={<Public />}
            endIcon={<ArrowDropDown />}
            onClick={(e) => setCountryMenuAnchor(e.currentTarget)}
          >
            {selectedCountryCodes.size > 0 ? `Countries (${selectedCountryCodes.size})` : 'Country'}
          </Button> */}
        <Menu
          anchorEl={countryMenuAnchor}
          open={Boolean(countryMenuAnchor)}
          onClose={() => setCountryMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ paper: { sx: { maxHeight: 360, minWidth: 260 } } }}
        >
          <Stack px={2} py={1}>
            <Typography variant="subtitle2">Countries</Typography>
            <Typography variant="caption" color="text.secondary">
              Select one or more; leave empty for all countries.
            </Typography>
          </Stack>
          {countryOptions.map(({ code, label }) => {
            const isActive = selectedCountryCodes.has(code);
            return (
              <MenuItem
                key={code}
                onClick={() => toggleCountry(code)}
                disableRipple
                dense
              >
                <Switch
                  checked={isActive}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleCountry(code);
                  }}
                />
                <ListItemText primary={label} secondary={code} secondaryTypographyProps={{ variant: 'caption' }} />
              </MenuItem>
            );
          })}
        </Menu>
      </Stack>

      <SalesOnboardingOverlay>
        <SalesChart
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          marketplaces={selectedMarketplaceIdsArray}
          countries={selectedCountryCodesArray}
        />
      </SalesOnboardingOverlay>

      <Paper sx={{ mt: 4 }}>
        <Stack
          position="sticky"
          sx={{ backgroundColor: 'background.paper' }}
          borderRadius={2}
          top={0}
          zIndex={99}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          p={2}
          borderBottom={1}
          borderColor="divider"
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <ProductsFilter />
            <ActiveFilters />
          </Stack>

          <Button
            variant="text"
            startIcon={isExporting ? <CircularProgress size={16} /> : <Icon icon="Download01" />}
            size="small"
            onClick={handleExportCSV}
            disabled={isExporting}
          >
            Export CSV
          </Button>
        </Stack>

        <SalesTable
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onProductSelect={navigateToProduct}
          marketplaces={selectedMarketplaceIdsArray}
          countries={selectedCountryCodesArray}
        />
      </Paper>
    </PageLayout>
  );
};

export default Sales;
